import React, { useEffect, useMemo, useState } from "react";
import { CalendarClock, Check, Sparkles } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { ANDROID_BETA_URL, APP_STORE_EXPECTED_AT, APP_STORE_URL, GOOGLE_PLAY_URL, MOBILE_STORE_LINKS_READY } from "@/constants/site";
import { captureEvent } from "@/lib/analytics";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const destinationProps = (storeUrl, betaUrl, store, placement) => ({
  href: storeUrl || betaUrl,
  ...(storeUrl ? { target: "_blank", rel: "noreferrer" } : {}),
  onClick: () => captureEvent(storeUrl ? "mobile_store_clicked" : "mobile_beta_requested", { store, placement }),
});

export const GooglePlayBadge = ({ testId = LANDING.heroPlayBadge, href = GOOGLE_PLAY_URL, placement = "hero" }) => (
  <a
    {...destinationProps(href, ANDROID_BETA_URL, "google_play", placement)}
    data-testid={testId}
    aria-label={href ? "Get Shopbook on Google Play" : "Request Shopbook Android beta access"}
    className="inline-flex min-h-[68px] min-w-0 items-center justify-center gap-2.5 rounded-2xl bg-[#132323] px-3 py-3 text-white shadow-lg shadow-black/10 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0E6B6B] sm:min-h-0 sm:justify-start sm:gap-3 sm:px-5"
  >
    <svg viewBox="0 0 24 24" className="h-7 w-7" aria-hidden="true">
      <path fill="#34A853" d="M3.6 20.4 14 12 3.6 3.6c-.4.3-.6.8-.6 1.4v14c0 .6.2 1.1.6 1.4z"/>
      <path fill="#FBBC04" d="M17.1 8.6 14 12l3.1 3.4 3.6-2.1c1-.6 1-2 0-2.6z"/>
      <path fill="#EA4335" d="M14 12 3.6 3.6c.2-.2.5-.3.9-.3.3 0 .6.1.9.2L17.1 8.6z"/>
      <path fill="#4285F4" d="M14 12 5.4 20.5c-.3.1-.6.2-.9.2-.4 0-.7-.1-.9-.3L14 12z"/>
    </svg>
    <span className="flex min-w-0 flex-col whitespace-nowrap text-left leading-tight">
      <span className="text-[10px] uppercase tracking-widest text-white/70">{href ? "Get it on" : "Request access"}</span>
      <span className="text-base font-semibold font-display">{href ? "Google Play" : "Android beta"}</span>
    </span>
  </a>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-7 w-7 fill-white" aria-hidden="true">
    <path d="M16.5 1.6c0 1.2-.5 2.4-1.3 3.2-.9.9-2.3 1.6-3.5 1.5-.2-1.2.4-2.5 1.2-3.3.9-.9 2.4-1.5 3.6-1.4zM20.3 17c-.5 1.2-.8 1.8-1.5 2.9-1 1.5-2.4 3.4-4.1 3.4-1.5 0-1.9-1-3.9-1-2 0-2.5 1-4 1-1.7 0-3-1.7-4-3.2C-.1 16.3-.4 11 1.5 8.2 2.9 6.2 5 5 7 5c1.8 0 2.9 1 4.4 1 1.4 0 2.3-1 4.4-1 1.6 0 3.2.9 4.4 2.3-3.9 2.1-3.3 7.7.1 9.7z"/>
  </svg>
);

const appStoreBadgeClasses = "inline-flex min-h-[68px] min-w-0 items-center justify-center gap-2.5 rounded-2xl bg-[#132323] px-3 py-3 text-white shadow-lg shadow-black/10 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0E6B6B] sm:min-h-0 sm:justify-start sm:gap-3 sm:px-5";

const timeUntil = (target) => {
  const remaining = Math.max(0, new Date(target).getTime() - Date.now());
  return {
    days: Math.floor(remaining / 86_400_000),
    hours: Math.floor((remaining / 3_600_000) % 24),
    minutes: Math.floor((remaining / 60_000) % 60),
    seconds: Math.floor((remaining / 1_000) % 60),
  };
};

const CountdownUnit = ({ label, value }) => (
  <div className="min-w-0 rounded-2xl border border-white/15 bg-white/10 px-2 py-3 text-center backdrop-blur-sm sm:px-3">
    <span className="block font-display text-2xl font-semibold tabular-nums text-white sm:text-3xl">
      {String(value).padStart(2, "0")}
    </span>
    <span className="mt-1 block text-[10px] font-semibold uppercase tracking-[0.16em] text-white/65">
      {label}
    </span>
  </div>
);

const AppleComingSoonDialog = ({ placement, testId }) => {
  const [open, setOpen] = useState(false);
  const [countdown, setCountdown] = useState(() => timeUntil(APP_STORE_EXPECTED_AT));
  const releaseDate = useMemo(
    () => new Intl.DateTimeFormat("en", { day: "numeric", month: "long", year: "numeric" }).format(new Date(APP_STORE_EXPECTED_AT)),
    [],
  );

  useEffect(() => {
    if (!open) return undefined;
    setCountdown(timeUntil(APP_STORE_EXPECTED_AT));
    const timer = window.setInterval(() => setCountdown(timeUntil(APP_STORE_EXPECTED_AT)), 1_000);
    return () => window.clearInterval(timer);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          aria-label="See when Shopbook will be available on the App Store"
          className={appStoreBadgeClasses}
          onClick={() => captureEvent("app_store_availability_viewed", { store: "app_store", placement })}
        >
          <AppleIcon />
          <span className="flex min-w-0 flex-col whitespace-nowrap text-left leading-tight">
            <span className="text-[10px] text-white/70">Coming soon to the</span>
            <span className="text-base font-semibold font-display">App Store</span>
          </span>
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[calc(100dvh-1.5rem)] w-[calc(100%-1.5rem)] max-w-xl overflow-y-auto border-0 bg-transparent p-0 shadow-2xl [&>button]:right-4 [&>button]:top-4 [&>button]:z-10 [&>button]:text-white sm:w-[calc(100%-2rem)] sm:rounded-[28px] sm:[&>button]:right-5 sm:[&>button]:top-5">
        <div className="relative overflow-hidden rounded-[24px] bg-[#0B2828] text-white sm:rounded-[26px]">
          <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-[#39C6A3]/20 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-[#F4B860]/20 blur-2xl" />

          <div className="relative px-5 pb-5 pt-7 sm:px-9 sm:pb-9 sm:pt-9">
            <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-[20px] border border-white/15 bg-white/10 shadow-xl backdrop-blur-sm sm:mb-6 sm:h-16 sm:w-16 sm:rounded-[22px]">
              <AppleIcon />
            </div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#74E1C4]/25 bg-[#74E1C4]/10 px-3 py-1.5 text-xs font-semibold text-[#A8F0DD]">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              iPhone release in progress
            </div>
            <DialogTitle className="max-w-[13ch] font-display text-[1.75rem] font-semibold leading-tight tracking-tight sm:max-w-none sm:text-4xl">
              Shopbook for iPhone is almost here.
            </DialogTitle>
            <DialogDescription className="mt-3 max-w-md text-sm leading-6 text-white/70 sm:text-base">
              We’re carefully preparing the Apple release so your experience is smooth, secure, and ready for business from day one.
            </DialogDescription>

            <div className="mt-6 grid grid-cols-2 gap-2 sm:mt-7 sm:grid-cols-4 sm:gap-3" aria-label={`Countdown to ${releaseDate}`} aria-live="off">
              <CountdownUnit label="Days" value={countdown.days} />
              <CountdownUnit label="Hours" value={countdown.hours} />
              <CountdownUnit label="Minutes" value={countdown.minutes} />
              <CountdownUnit label="Seconds" value={countdown.seconds} />
            </div>

            <div className="mt-5 flex items-center gap-3 rounded-2xl border border-white/10 bg-black/10 px-4 py-3.5">
              <CalendarClock className="h-5 w-5 shrink-0 text-[#74E1C4]" aria-hidden="true" />
              <p className="text-sm text-white/75">
                Expected availability: <span className="font-semibold text-white">{releaseDate}</span>
              </p>
            </div>

            <DialogClose asChild>
              <button
                type="button"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#58D5B4] px-5 py-3.5 font-semibold text-[#092524] transition hover:bg-[#74E1C4] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0B2828]"
              >
                <Check className="h-4 w-4" aria-hidden="true" />
                Got it — I’ll check back soon
              </button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AppStoreBadge = ({ testId = LANDING.heroAppStoreBadge, href = APP_STORE_URL, placement = "hero" }) => {
  if (!href) {
    return <AppleComingSoonDialog placement={placement} testId={testId} />;
  }

  return (
    <a
      {...destinationProps(href, "", "app_store", placement)}
      data-testid={testId}
      aria-label="Download Shopbook on the App Store"
      className={appStoreBadgeClasses}
    >
      <AppleIcon />
      <span className="flex min-w-0 flex-col whitespace-nowrap text-left leading-tight">
        <span className="text-[10px] text-white/70">Download on the</span>
        <span className="text-base font-semibold font-display">App Store</span>
      </span>
    </a>
  );
};

export const AppStoreBadges = ({ playId, storeId, placement = "hero", showAvailabilityNote = false }) => {
  if (!MOBILE_STORE_LINKS_READY && placement !== "hero") return null;

  return (
    <div id={placement === "hero" ? "mobile-apps" : undefined} data-testid="app-store-badges-group">
      <div className="grid grid-cols-1 gap-3 min-[390px]:grid-cols-2 sm:flex sm:flex-wrap [&>*]:w-full min-[390px]:[&>*]:w-auto min-[390px]:[&>*]:min-w-0 sm:[&>*]:w-auto">
        <GooglePlayBadge testId={playId} placement={placement} />
        <AppStoreBadge testId={storeId} placement={placement} />
      </div>
      {showAvailabilityNote && !MOBILE_STORE_LINKS_READY ? (
        <p className="mt-2 text-xs text-[#4B5E5E]">The web app is available now. Request mobile beta access while the official store releases are prepared.</p>
      ) : null}
    </div>
  );
};
